import { RefObject } from "react";
import { useState, useEffect } from "react";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { disablingRules } from "./ssml-disabling-rules";
import { getSelectionTagHierarchy } from "./selection-tag-hierarchy";
import { prosodyAttributes, SSMLTag } from "shared/constants";

type TinyMCEEditor = NonNullable<EditorRef["editor"]>;

/**
 * Creates a text-based range for an element to allow accurate comparison with the selection range.
 */
const createTextRangeFromElement = (editor: TinyMCEEditor, element: HTMLElement): Range | null => {
  const treeWalker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node: Node) => {
        return node.nodeValue && node.nodeValue.length > 0
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    }
  );

  const firstTextNode = treeWalker.nextNode() as Text | null;
  if (!firstTextNode) return null;

  let lastTextNode = firstTextNode;
  while (treeWalker.nextNode()) {
    lastTextNode = treeWalker.currentNode as Text;
  }

  const range = editor.dom.createRng();
  range.setStart(firstTextNode, 0);
  range.setEnd(lastTextNode, lastTextNode.nodeValue?.length || 0);
  
  return range;
};

/**
 * Gets the nearest SSML ancestor and checks if the current selection fully covers it.
 */
const getEnclosingTagStatus = (
  editorRef: RefObject<EditorRef | null>
): { tagType: SSMLTag; element: HTMLElement; isFullySelected: boolean } | null => {
  const editor = editorRef.current?.editor;
  if (!editor) return null;

  const selectedNode = editor.selection?.getNode() as Element | null;
  const ssmlWrapper = selectedNode?.closest?.("span[data-ssml]") as HTMLElement | null;
  
  if (!ssmlWrapper) return null;

  const dataAttribute = ssmlWrapper.getAttribute("data-ssml") || "";
  const tagType = (dataAttribute.split("-")[0] as SSMLTag) || null;
  
  if (!tagType) return null;

  // Compare selection range vs wrapper text range
  const currentSelectionRange = editor.selection.getRng();
  const wrapperTextRange = createTextRangeFromElement(editor, ssmlWrapper);

  if (!wrapperTextRange) {
    return { tagType, element: ssmlWrapper, isFullySelected: false };
  }

  const startsBeforeOrAt = currentSelectionRange.compareBoundaryPoints(Range.START_TO_START, wrapperTextRange) <= 0;
  const endsAfterOrAt = currentSelectionRange.compareBoundaryPoints(Range.END_TO_END, wrapperTextRange) >= 0;

  return { tagType, element: ssmlWrapper, isFullySelected: startsBeforeOrAt && endsAfterOrAt };
};

/**
 * Gets the tags present in the current selection
 */
const getTagsInSelection = (
  editorRef: RefObject<EditorRef | null>
): Set<SSMLTag> => {
  const editor = editorRef.current?.editor;
  if (!editor) return new Set();

  const selection = editor.selection;
  if (!selection) return new Set();

  const selectedContent = selection.getContent({ format: "html" });
  const tagsInSelection = new Set<SSMLTag>();

  if (selectedContent) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = selectedContent;

    const ssmlSpans = tempDiv.querySelectorAll("span[data-ssml]");
    for (const span of ssmlSpans) {
      const dataSSML = span.getAttribute("data-ssml");
      if (dataSSML) {
        const tagType = dataSSML.split("-")[0] as SSMLTag;
        tagsInSelection.add(tagType);
      }
    }
  }

  if (tagsInSelection.size === 0) {
    const enclosingTag = getEnclosingTagStatus(editorRef);
    if (enclosingTag && enclosingTag.isFullySelected) {
      tagsInSelection.add(enclosingTag.tagType);
    }
  }

  return tagsInSelection;
};

/**
 * Determines if a specific SSML menu should be disabled based on the current selection
 *
 * @param editorRef - React ref to the TinyMCE editor
 * @param menuTagType - The SSML tag type corresponding to the menu (e.g., SSMLTag.Voice)
 * @returns boolean - True if the menu should be disabled
 */
export const shouldDisableSSMLMenu = (
  editorRef: RefObject<EditorRef | null>,
  menuTagType: SSMLTag
): boolean => {
  const { hasMultipleChars, tagHierarchy } = getSelectionTagHierarchy(editorRef);

  const enclosingTag = getEnclosingTagStatus(editorRef);
  const isInsideLeafTag = enclosingTag?.tagType === SSMLTag.SayAs || enclosingTag?.tagType === SSMLTag.Phoneme;

  if (isInsideLeafTag && enclosingTag) {
    if (!enclosingTag.isFullySelected) {
      if (menuTagType !== enclosingTag.tagType && menuTagType !== SSMLTag.Break) {
        return true;
      }
    }
  }

  // If no multi-character selection, don't disable based on tag hierarchy
  if (!hasMultipleChars) return false;

  // Get tags present in the selection
  const tagsInSelection = getTagsInSelection(editorRef);

  // If there are tags in the selection, use the new enabling rules
  if (tagsInSelection.size > 0) {
    const editor = editorRef.current?.editor;
    const selectedContent = editor?.selection.getContent({ format: "html" }) || "";
    return shouldDisableBasedOnSelectionTags(
      tagsInSelection,
      menuTagType,
      selectedContent
    );
  }

  // Check if selection crosses SSML tag boundaries (only for non-tagged selections)
  if (selectionCrossesSsmlBoundaries(editorRef)) {
    return true;
  }

  // Get the disabling rules for this menu type
  const disablingTagTypes = disablingRules[menuTagType] || [];

  // Special handling for Prosody menu - check if any prosody attributes are present
  if (menuTagType === SSMLTag.Prosody) {
    // Check if any prosody attribute tags are in the hierarchy
    return tagHierarchy.some(
      (tag) =>
        prosodyAttributes.includes(tag.tagType) ||
        disablingTagTypes.includes(tag.tagType)
    );
  }

  // Check if any of the tags in the hierarchy are in the disabling rules
  return tagHierarchy.some((tag) => disablingTagTypes.includes(tag.tagType));
};

/**
 * Determines if a menu should be disabled based on the tags present in the selection
 */
const shouldDisableBasedOnSelectionTags = (
  tagsInSelection: Set<SSMLTag>,
  menuTagType: SSMLTag,
  selectedContent: string
): boolean => {
  const tagsArray = Array.from(tagsInSelection);

  // No self nesting anywhere
  if (tagsInSelection.has(menuTagType)) return true;

  // Prosody Attributes Check
  if (menuTagType === SSMLTag.Prosody) {
    const hasProsodyAttribute = tagsArray.some((tag) =>
      prosodyAttributes.includes(tag)
    );
    if (hasProsodyAttribute) return true;
  }
  if (menuTagType === SSMLTag.SayAs || menuTagType === SSMLTag.Phoneme) {
    return tagsArray.length > 0;
  }

  const forbiddenChildren = disablingRules[menuTagType] || [];
  const hasForbiddenChild = tagsArray.some((t) => forbiddenChildren.includes(t));
  if (hasForbiddenChild) return true;

  if (selectedContent) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = selectedContent;

    const ssmlSpans = tempDiv.querySelectorAll("span[data-ssml]");
    ssmlSpans.forEach((span) => span.remove());

    const textOutsideTags = tempDiv.textContent || "";
    const hasOnlyWhitespaceOutside = textOutsideTags.trim().length === 0;

    if (hasOnlyWhitespaceOutside) {
      return menuTagType === SSMLTag.Break ? true : false;
    }
  }

  return false;
};

/**
 * Custom hook that determines if a specific SSML menu should be disabled
 */
export const useSSMLMenuDisabled = (
  editorRef: RefObject<EditorRef | null>,
  menuTagType: SSMLTag
) => {
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  useEffect(() => {
    const editor = editorRef.current?.editor;
    if (!editor) return;

    const updateDisabledState = () => {
      const selection = editor.selection;
      if (!selection) {
        setIsDisabled(false);
        return;
      }

      const selectedContent = selection.getContent({ format: "text" });

      // If no text is selected, don't disable
      if (!selectedContent || selectedContent.trim().length === 0) {
        setIsDisabled(false);
        return;
      }

      // Get tags present in the selection
      const tagsInSelection = getTagsInSelection(editorRef);

      // If there are tags in the selection, use the selection-based rules
      if (tagsInSelection.size > 0) {
        const selectedHtmlContent = selection.getContent({ format: "html" });
        const isDisabledBySelectionTags = shouldDisableBasedOnSelectionTags(
          tagsInSelection,
          menuTagType,
          selectedHtmlContent
        );
        setIsDisabled(isDisabledBySelectionTags);
        return;
      }

      // For selections without tags, check hierarchy and boundary conditions
      const isInsideDisallowedTag = shouldDisableSSMLMenu(
        editorRef,
        menuTagType
      );
      const containsIncompatibleTags = shouldDisableSSMLMenuForSelection(
        editorRef,
        menuTagType
      );
      const crossesBoundaries = selectionCrossesSsmlBoundaries(editorRef);

      setIsDisabled(
        isInsideDisallowedTag || containsIncompatibleTags || crossesBoundaries
      );
    };

    // Listen for selection changes
    editor.on("SelectionChange", updateDisabledState);
    editor.on("mouseup", updateDisabledState);
    editor.on("keyup", updateDisabledState);

    // Initial check
    updateDisabledState();

    return () => {
      // Clean up event listeners
      editor.off("SelectionChange", updateDisabledState);
      editor.off("mouseup", updateDisabledState);
      editor.off("keyup", updateDisabledState);
    };
  }, [editorRef, menuTagType]);

  return isDisabled;
};

/**
 * Checks if the current selection contains any SSML tags that would make adding
 * a new surrounding tag invalid based on SSML nesting rules.
 *
 * @param editorRef - React ref to the TinyMCE editor
 * @param menuTagType - The SSML tag type we want to add (e.g., SSMLTag.Voice)
 * @returns boolean - True if the menu should be disabled
 */
export const shouldDisableSSMLMenuForSelection = (
  editorRef: RefObject<EditorRef | null>,
  menuTagType: SSMLTag
): boolean => {
  const editor = editorRef.current?.editor;
  if (!editor) return false;

  // Get current selection
  const selection = editor.selection;
  if (!selection) return false;

  // Get selected HTML content
  const selectedContent = selection.getContent({ format: "html" });
  if (!selectedContent) return false;

  // Creating a temporary element to parse the selected content
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = selectedContent;

  // Find all spans with data-ssml attribute in the selection
  const ssmlSpans = tempDiv.querySelectorAll("span[data-ssml]");
  if (ssmlSpans.length === 0) return false;

  // Get tags present in the selection
  const tagsInSelection = getTagsInSelection(editorRef);

  if (tagsInSelection.size > 0) {
    return shouldDisableBasedOnSelectionTags(
      tagsInSelection,
      menuTagType,
      selectedContent
    );
  }

  return false;
};

/**
 * Checks if the current text selection boundaries cross any SSML tag boundaries,
 * which would create invalid nesting if another tag was applied
 *
 * @param editorRef - React ref to the TinyMCE editor
 * @returns boolean - True if selection crosses tag boundaries
 */
export const selectionCrossesSsmlBoundaries = (
  editorRef: RefObject<EditorRef | null>
): boolean => {
  const editor = editorRef.current?.editor;
  if (!editor) return false;

  const selection = editor.selection;
  if (!selection) return false;

  const range = selection.getRng();

  // Get the start and end containers of the selection
  const startContainer = range.startContainer;
  const endContainer = range.endContainer;

  // If selection is within a single text node, no boundaries are crossed
  if (
    startContainer === endContainer &&
    startContainer.nodeType === Node.TEXT_NODE
  ) {
    return false;
  }

  // Create a function to get all parent SSML tags for a node
  const getParentSSMLTags = (node: Node): Set<Node> => {
    const tags = new Set<Node>();
    let current: Node | null = node;

    while (current && current !== editor.getBody()) {
      if (current.nodeType === Node.ELEMENT_NODE) {
        const element = current as Element;
        if (element.getAttribute && element.getAttribute("data-ssml")) {
          tags.add(current);
        }
      }
      current = current.parentNode;
    }

    return tags;
  };

  // Get SSML tags for start and end containers
  const startTags = getParentSSMLTags(startContainer);
  const endTags = getParentSSMLTags(endContainer);

  // If both containers are in the same set of tags, then boundaries aren't crossed
  if (startTags.size === endTags.size) {
    // Check if they're the same tags
    const startTagsArray = Array.from(startTags);
    const endTagsArray = Array.from(endTags);

    for (let i = 0; i < startTagsArray.length; i++) {
      if (startTagsArray[i] !== endTagsArray[i]) {
        return true; // Different tags at same level, boundaries crossed
      }
    }

    return false; // Same tags, no boundaries crossed
  }

  // Different number of parent tags means boundaries are crossed
  return true;
};
